/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Minus, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import apiService from "@/services/apiService";
import { getClosestPoliceStation } from "@/lib/helper";
import useErrorStore from "@/store/confirmationStore";
import { validateAge, validateDate, validateDateRange, validateNumber, validateRequired } from "@/lib/validationHelper";
import { showValidationToast } from "@/lib/toastHelper";
import { getNestedValue, setNestedError } from "@/lib/utils";
import { getUserInfo } from "@/services/userService";

export interface Accused {
  name: string;
  father_name: string;
  age: string;
  address: string;
}

export interface BoundDownOrderCreate {
  meaning: string;
  dateOfOrder: string;
  duration: string;
  bondAmount: number;
  amountPerSurety: number;
  totalSureties: number;
  suretyConditions: string;
  consequenceOnDefault: string;
  summary: string;
  effectiveFrom: string;
  effectiveTo: string;
  createdBy?: { connect: { id: string } };
}

export interface CrpcBondOrderForm {
  id?: string;
  caseNumber: string;
  court: string;
  policeStation: string;
  district: string;
  dateOfOrder: string;
  sectionsApplied: string;
  village: string;
  policeStationArea: string;
  districtArea: string;
  accusedList: Accused[];
  description: string;
  boundDownOrderId?: string;
  boundDownOrder?: BoundDownOrderCreate;
  file_name?: string;
  documentType?: string;
  document?: { connect: { id: string } };
  createdBy?: { connect: { id: string } };
  parentDocumentId?: string;
}

interface ValidationErrors {
  [key: string]: string | ValidationErrors | ValidationErrors[];
}

interface CrpcBondOrderPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data?: Partial<CrpcBondOrderForm>;
}

export function CrpcBondOrderPopup({ open, onOpenChange, data }: CrpcBondOrderPopupProps) {
  const showError = useErrorStore((s) => s.showError);
  const [formData, setFormData] = useState<Partial<CrpcBondOrderForm>>({
    accusedList: [{ name: "", father_name: "", age: "", address: "" }],
    boundDownOrder: {
      meaning: "",
      dateOfOrder: "",
      duration: "",
      bondAmount: 0,
      amountPerSurety: 0,
      totalSureties: 0,
      suretyConditions: "",
      consequenceOnDefault: "",
      summary: "",
      effectiveFrom: "",
      effectiveTo: "",
    },
  });
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  useEffect(() => {
    if (data) {
      setFormData({
        accusedList: data.accusedList || [{ name: "", father_name: "", age: "", address: "" }],
        boundDownOrder: {
          meaning: data.boundDownOrder?.meaning ?? "",
          dateOfOrder: data.boundDownOrder?.dateOfOrder ?? "",
          duration: data.boundDownOrder?.duration ?? "",
          bondAmount: data.boundDownOrder?.bondAmount ?? 0,
          amountPerSurety: data.boundDownOrder?.amountPerSurety ?? 0,
          totalSureties: data.boundDownOrder?.totalSureties ?? 0,
          suretyConditions: data.boundDownOrder?.suretyConditions ?? "",
          consequenceOnDefault: data.boundDownOrder?.consequenceOnDefault ?? "",
          summary: data.boundDownOrder?.summary ?? "",
          effectiveFrom: data.boundDownOrder?.effectiveFrom ?? "",
          effectiveTo: data.boundDownOrder?.effectiveTo ?? "",
        },
        ...data,
      });
    }
  }, [data]);

  const validateField = (field: string, value: any, context?: any): string => {
    switch (field) {
      case "caseNumber":
        return validateRequired(value, "Case Number");
      case "court":
        return validateRequired(value, "Court");
      case "policeStation":
        return validateRequired(value, "Police Station");
      case "district":
        return validateRequired(value, "District");
      case "dateOfOrder":
        return validateDate(value, "Date of Order");
      case "sectionsApplied":
        return validateRequired(value, "Sections Applied");
      case "village":
        return validateRequired(value, "Village");
      case "policeStationArea":
        return validateRequired(value, "Police Station Area");
      case "districtArea":
        return validateRequired(value, "District Area");
      case "description":
        return validateRequired(value, "Description");

      // Accused validations
      case "accused.name":
        return validateRequired(value, "Accused Name");
      // case "accused.father_name":
        // return value ? "" : "Father's name is required";
      case "accused.age":
        return validateAge(value);
      // case "accused.address":
        // return validateRequired(value, "Address");

      // Bound down order validations
      case "boundDownOrder.meaning":
        return validateRequired(value, "Meaning");
      case "boundDownOrder.dateOfOrder":
        return validateDate(value, "Date of Order");
      case "boundDownOrder.duration":
        return validateRequired(value, "Duration");
      case "boundDownOrder.bondAmount":
        return validateNumber(value, "Bond Amount", 0);
      case "boundDownOrder.amountPerSurety":
        return validateNumber(value, "Amount Per Surety", 0);
      case "boundDownOrder.totalSureties":
        return validateNumber(value, "Total Sureties", 1);
      case "boundDownOrder.suretyConditions":
        return validateRequired(value, "Surety Conditions");
      case "boundDownOrder.consequenceOnDefault":
        return validateRequired(value, "Consequence on Default");
      case "boundDownOrder.summary":
        return validateRequired(value, "Summary");
      case "boundDownOrder.effectiveFrom":
        return validateDate(value, "Effective From Date");
      case "boundDownOrder.effectiveTo": {
        const fromDate = context?.effectiveFrom || formData.boundDownOrder?.effectiveFrom;
        const dateError = validateDate(value, "Effective To Date");
        if (dateError) return dateError;
        return validateDateRange(fromDate, value);
      }

      default:
        return "";
    }
  };

  const setFieldError = (fieldPath: string, error: string) => {
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      const keys = fieldPath.split(".");

      if (keys.length === 1) {
        if (error) {
          newErrors[keys[0]] = error;
        } else {
          delete newErrors[keys[0]];
        }
      } else if (keys.length === 2) {
        if (!newErrors[keys[0]]) newErrors[keys[0]] = {};
        if (error) {
          (newErrors[keys[0]] as any)[keys[1]] = error;
        } else {
          delete (newErrors[keys[0]] as any)[keys[1]];
          if (Object.keys(newErrors[keys[0]] as any).length === 0) {
            delete newErrors[keys[0]];
          }
        }
      } else if (keys.length === 3 && keys[0] === "accusedList") {
        const index = parseInt(keys[1]);
        if (!Array.isArray(newErrors.accusedList)) {
          newErrors.accusedList = [];
        }
        if (!newErrors.accusedList[index]) {
          newErrors.accusedList[index] = {};
        }
        if (error) {
          (newErrors.accusedList[index] as any)[keys[2]] = error;
        } else {
          delete (newErrors.accusedList[index] as any)[keys[2]];
          if (Object.keys(newErrors.accusedList[index] as any).length === 0) {
            delete newErrors.accusedList[index];
          }
        }
      }

      return newErrors;
    });
  };

  const getFieldError = (fieldPath: string): string => {
    const keys = fieldPath.split(".");
    let current: any = validationErrors;

    for (const key of keys) {
      if (current && typeof current === "object") {
        current = current[key];
      } else {
        return "";
      }
    }

    return typeof current === "string" ? current : "";
  };

  const handleFieldBlur = (fieldPath: string, value: any, context?: any) => {
    const error = validateField(fieldPath, value, context);
    setFieldError(fieldPath, error);
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};
    let hasErrors = false;

    // Validate all required fields
    const requiredFields = [
      "caseNumber", "court", "policeStation", "district", "dateOfOrder", 
      "sectionsApplied", "village", "policeStationArea", "districtArea", "description",
      "boundDownOrder.meaning", "boundDownOrder.dateOfOrder", "boundDownOrder.duration",
      "boundDownOrder.bondAmount", "boundDownOrder.amountPerSurety", "boundDownOrder.totalSureties",
      "boundDownOrder.suretyConditions", "boundDownOrder.consequenceOnDefault",
      "boundDownOrder.summary", "boundDownOrder.effectiveFrom", "boundDownOrder.effectiveTo"
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, getNestedValue(formData, field));
      if (error) {
        setNestedError(errors, field, error);
        hasErrors = true;
      }
    });

    // Validate accused list
    if (formData.accusedList) {
      formData.accusedList.forEach((accused, index) => {
        ["name","age"].forEach((field) => {
          const error = validateField(`accused.${field}`, getNestedValue(accused, field));
          if (error) {
            if (!Array.isArray(errors.accusedList)) errors.accusedList = [];
            if (!errors.accusedList[index]) errors.accusedList[index] = {};
            (errors.accusedList[index] as any)[field] = error;
            hasErrors = true;
          }
        });
      });
    }

    setValidationErrors(errors);
    return !hasErrors;
  };

  const handleInputChange = (field: keyof CrpcBondOrderForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccusedChange = (index: number, key: keyof Accused, value: any) => {
    const updated = [...(formData.accusedList || [])];
    updated[index] = { ...updated[index], [key]: value };
    setFormData((prev) => ({ ...prev, accusedList: updated }));
  };

  const addAccused = () => {
    setFormData((prev) => ({
      ...prev,
      accusedList: [...(prev.accusedList || []), { name: "", father_name: "", age: "", address: "" }],
    }));
  };

  const removeAccused = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      accusedList: prev.accusedList?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleBoundDownOrderChange = (key: keyof BoundDownOrderCreate, value: any) => {
    setFormData((prev) => ({
      ...prev,
      boundDownOrder: {
        meaning: prev.boundDownOrder?.meaning ?? "",
        dateOfOrder: prev.boundDownOrder?.dateOfOrder ?? "",
        duration: prev.boundDownOrder?.duration ?? "",
        bondAmount: prev.boundDownOrder?.bondAmount ?? 0,
        amountPerSurety: prev.boundDownOrder?.amountPerSurety ?? 0,
        totalSureties: prev.boundDownOrder?.totalSureties ?? 0,
        suretyConditions: prev.boundDownOrder?.suretyConditions ?? "",
        consequenceOnDefault: prev.boundDownOrder?.consequenceOnDefault ?? "",
        summary: prev.boundDownOrder?.summary ?? "",
        effectiveFrom: prev.boundDownOrder?.effectiveFrom ?? "",
        effectiveTo: prev.boundDownOrder?.effectiveTo ?? "",
        [key]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showValidationToast(validationErrors);
      return;
    }

    const postParams = JSON.parse(JSON.stringify(formData));
    const policeStation: any = getClosestPoliceStation(postParams.policeStation);
    postParams.dateOfOrder = postParams.dateOfOrder ? new Date(postParams.dateOfOrder).toISOString() : "";
    if (postParams.boundDownOrder) {
      postParams.boundDownOrder.dateOfOrder = postParams.boundDownOrder.dateOfOrder ? new Date(postParams.boundDownOrder.dateOfOrder).toISOString() : "";
      postParams.boundDownOrder.effectiveFrom = postParams.boundDownOrder.effectiveFrom ? new Date(postParams.boundDownOrder.effectiveFrom).toISOString() : "";
      postParams.boundDownOrder.effectiveTo = postParams.boundDownOrder.effectiveTo ? new Date(postParams.boundDownOrder.effectiveTo).toISOString() : "";
    }
    postParams.policeStation = policeStation.name;
    postParams.document = {
      connect: { id: postParams.parentDocumentId || "" },
    };
    delete postParams.file_name;
    delete postParams.parentDocumentId;
    delete postParams.documentType;
    const userInfo:any = getUserInfo();
    if (userInfo) {
      postParams.createdBy = {
        connect: { id: userInfo.id },
      };
      if (postParams.boundDownOrder)
        postParams.boundDownOrder.createdBy = {
          connect: { id: userInfo.id },
        };
    }

    const payload = {
      ...postParams,
      boundDownOrder: { create: postParams.boundDownOrder },
    };

    try {
      const response = await apiService.post('crpc-bond-order', JSON.stringify(payload));
      if (response) {
        toast.success("CrPC Bond Order saved successfully!", {
          description: "The bond order has been saved.",
          action: { label: "Ok", onClick: () => console.log("Ok") },
        });
        onOpenChange(false);
      } else {
        showError("Failed to save bond order. Please check the form and try again.");
      }
    } catch (error: any) {
      showError(error);
    }
  };

  const ErrorMessage = ({ error }: { error: string }) =>
    error ? (
      <div className="flex items-center gap-1 text-red-600 text-sm mt-1">
        <AlertCircle size={14} />
        <span>{error}</span>
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="h-[95vh] bg-white flex flex-col w-[95%]" style={{ maxWidth: "1200px" }}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-black">CrPC Bond Order</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-4">
          {/* Basic Case Info */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Case Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Case Number</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.caseNumber || ""} 
                  onChange={(e) => handleInputChange("caseNumber", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("caseNumber", e.target.value)}
                  className={getFieldError("caseNumber") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("caseNumber")} />
              </div>
              <div>
                <Label>Court</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.court || ""} 
                  onChange={(e) => handleInputChange("court", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("court", e.target.value)}
                  className={getFieldError("court") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("court")} />
              </div>
              <div>
                <Label>Police Station</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.policeStation || ""} 
                  onChange={(e) => handleInputChange("policeStation", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("policeStation", e.target.value)}
                  className={getFieldError("policeStation") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("policeStation")} />
              </div>
              <div>
                <Label>District</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.district || ""} 
                  onChange={(e) => handleInputChange("district", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("district", e.target.value)}
                  className={getFieldError("district") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("district")} />
              </div>
              <div>
                <Label>Date of Order</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="date" 
                  value={formData.dateOfOrder || ""} 
                  onChange={(e) => handleInputChange("dateOfOrder", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("dateOfOrder", e.target.value)}
                  className={getFieldError("dateOfOrder") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("dateOfOrder")} />
              </div>
              <div>
                <Label>Sections Applied</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.sectionsApplied || ""} 
                  onChange={(e) => handleInputChange("sectionsApplied", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("sectionsApplied", e.target.value)}
                  className={getFieldError("sectionsApplied") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("sectionsApplied")} />
              </div>
              <div>
                <Label>Village</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.village || ""} 
                  onChange={(e) => handleInputChange("village", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("village", e.target.value)}
                  className={getFieldError("village") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("village")} />
              </div>
              <div>
                <Label>Police Station Area</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.policeStationArea || ""} 
                  onChange={(e) => handleInputChange("policeStationArea", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("policeStationArea", e.target.value)}
                  className={getFieldError("policeStationArea") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("policeStationArea")} />
              </div>
              <div>
                <Label>District Area</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.districtArea || ""} 
                  onChange={(e) => handleInputChange("districtArea", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("districtArea", e.target.value)}
                  className={getFieldError("districtArea") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("districtArea")} />
              </div>
            </CardContent>
          </Card>

          {/* Bound Down Order */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Bound Down Order</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Meaning</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.boundDownOrder?.meaning || ""} 
                  onChange={(e) => handleBoundDownOrderChange("meaning", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.meaning", e.target.value)}
                  className={getFieldError("boundDownOrder.meaning") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.meaning")} />
              </div>
              <div>
                <Label>Date of Order</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="date" 
                  value={formData.boundDownOrder?.dateOfOrder || ""} 
                  onChange={(e) => handleBoundDownOrderChange("dateOfOrder", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.dateOfOrder", e.target.value)}
                  className={getFieldError("boundDownOrder.dateOfOrder") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.dateOfOrder")} />
              </div>
              <div>
                <Label>Duration</Label>
                <Input 
                  disabled={isPreviewMode} 
                  value={formData.boundDownOrder?.duration || ""} 
                  onChange={(e) => handleBoundDownOrderChange("duration", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.duration", e.target.value)}
                  className={getFieldError("boundDownOrder.duration") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.duration")} />
              </div>
              <div>
                <Label>Bond Amount</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="number" 
                  value={formData.boundDownOrder?.bondAmount || ""} 
                  onChange={(e) => handleBoundDownOrderChange("bondAmount", Number(e.target.value))} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.bondAmount", e.target.value)}
                  className={getFieldError("boundDownOrder.bondAmount") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.bondAmount")} />
              </div>
              <div>
                <Label>Amount Per Surety</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="number" 
                  value={formData.boundDownOrder?.amountPerSurety || ""} 
                  onChange={(e) => handleBoundDownOrderChange("amountPerSurety", Number(e.target.value))} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.amountPerSurety", e.target.value)}
                  className={getFieldError("boundDownOrder.amountPerSurety") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.amountPerSurety")} />
              </div>
              <div>
                <Label>Total Sureties</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="number" 
                  value={formData.boundDownOrder?.totalSureties || ""} 
                  onChange={(e) => handleBoundDownOrderChange("totalSureties", Number(e.target.value))} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.totalSureties", e.target.value)}
                  className={getFieldError("boundDownOrder.totalSureties") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.totalSureties")} />
              </div>
              <div className="md:col-span-2">
                <Label>Surety Conditions</Label>
                <Textarea 
                  disabled={isPreviewMode} 
                  value={formData.boundDownOrder?.suretyConditions || ""} 
                  onChange={(e) => handleBoundDownOrderChange("suretyConditions", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.suretyConditions", e.target.value)}
                  className={getFieldError("boundDownOrder.suretyConditions") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.suretyConditions")} />
              </div>
              <div className="md:col-span-2">
                <Label>Consequence on Default</Label>
                <Textarea 
                  disabled={isPreviewMode} 
                  value={formData.boundDownOrder?.consequenceOnDefault || ""} 
                  onChange={(e) => handleBoundDownOrderChange("consequenceOnDefault", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.consequenceOnDefault", e.target.value)}
                  className={getFieldError("boundDownOrder.consequenceOnDefault") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.consequenceOnDefault")} />
              </div>
              <div className="md:col-span-2">
                <Label>Summary</Label>
                <Textarea 
                  disabled={isPreviewMode} 
                  value={formData.boundDownOrder?.summary || ""} 
                  onChange={(e) => handleBoundDownOrderChange("summary", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.summary", e.target.value)}
                  className={getFieldError("boundDownOrder.summary") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.summary")} />
              </div>
              <div>
                <Label>Effective From</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="date" 
                  value={formData.boundDownOrder?.effectiveFrom || ""} 
                  onChange={(e) => handleBoundDownOrderChange("effectiveFrom", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.effectiveFrom", e.target.value)}
                  className={getFieldError("boundDownOrder.effectiveFrom") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.effectiveFrom")} />
              </div>
              <div>
                <Label>Effective To</Label>
                <Input 
                  disabled={isPreviewMode} 
                  type="date" 
                  value={formData.boundDownOrder?.effectiveTo || ""} 
                  onChange={(e) => handleBoundDownOrderChange("effectiveTo", e.target.value)} 
                  onBlur={(e) => handleFieldBlur("boundDownOrder.effectiveTo", e.target.value, { effectiveFrom: formData.boundDownOrder?.effectiveFrom })}
                  className={getFieldError("boundDownOrder.effectiveTo") ? "border-red-500" : ""} 
                />
                <ErrorMessage error={getFieldError("boundDownOrder.effectiveTo")} />
              </div>
            </CardContent>
          </Card>

          {/* Accused List */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader className="flex justify-between items-center">
              <CardTitle className="text-lg text-black">Accused List</CardTitle>
              {!isPreviewMode && (
                <Button size="sm" onClick={addAccused} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                  <Plus className="h-4 w-4 mr-1" /> Add Accused
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {(formData.accusedList || []).map((accused, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium text-black">Accused {index + 1}</h4>
                    {!isPreviewMode && (
                      <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => removeAccused(index)}>
                        <Minus className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label>Name</Label>
                      <Input 
                        disabled={isPreviewMode} 
                        value={accused.name} 
                        onChange={(e) => handleAccusedChange(index, "name", e.target.value)} 
                        onBlur={(e) => handleFieldBlur(`accusedList.${index}.name`, e.target.value)}
                        className={getFieldError(`accusedList.${index}.name`) ? "border-red-500" : ""} 
                      />
                      <ErrorMessage error={getFieldError(`accusedList.${index}.name`)} />
                    </div>
                    <div>
                      <Label>Father's Name</Label>
                      <Input 
                        disabled={isPreviewMode} 
                        value={accused.father_name} 
                        onChange={(e) => handleAccusedChange(index, "father_name", e.target.value)} 
                        onBlur={(e) => handleFieldBlur(`accusedList.${index}.father_name`, e.target.value)}
                        className={getFieldError(`accusedList.${index}.father_name`) ? "border-red-500" : ""} 
                      />
                      <ErrorMessage error={getFieldError(`accusedList.${index}.father_name`)} />
                    </div>
                    <div>
                      <Label>Age</Label>
                      <Input 
                        disabled={isPreviewMode} 
                        value={accused.age} 
                        onChange={(e) => handleAccusedChange(index, "age", e.target.value)} 
                        onBlur={(e) => handleFieldBlur(`accusedList.${index}.age`, e.target.value)}
                        className={getFieldError(`accusedList.${index}.age`) ? "border-red-500" : ""} 
                      />
                      <ErrorMessage error={getFieldError(`accusedList.${index}.age`)} />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address</Label>
                      <Textarea 
                        disabled={isPreviewMode} 
                        value={accused.address} 
                        onChange={(e) => handleAccusedChange(index, "address", e.target.value)} 
                        onBlur={(e) => handleFieldBlur(`accusedList.${index}.address`, e.target.value)}
                        className={getFieldError(`accusedList.${index}.address`) ? "border-red-500" : ""} 
                      />
                      <ErrorMessage error={getFieldError(`accusedList.${index}.address`)} />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Description */}
          <Card className="border border-gray-200 mb-4">
            <CardHeader>
              <CardTitle className="text-lg text-black">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea 
                disabled={isPreviewMode} 
                rows={3} 
                value={formData.description || ""} 
                onChange={(e) => handleInputChange("description", e.target.value)} 
                onBlur={(e) => handleFieldBlur("description", e.target.value)}
                className={getFieldError("description") ? "border-red-500" : ""} 
              />
              <ErrorMessage error={getFieldError("description")} />
            </CardContent>
          </Card>
        </div>

        <div className="flex-shrink-0 flex justify-end space-x-2 pt-4 border-t border-gray-200 bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-gray-300">
            Cancel
          </Button>

          {isPreviewMode ? (
            <>
              <Button variant="outline" onClick={() => setIsPreviewMode(false)}>
                Edit Again
              </Button>
              <Button onClick={handleSubmit} className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white">
                Confirm & Submit
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                if (validateForm()) {
                  setTimeout(() => {
                    const scrollable = document.querySelector(".flex-1.overflow-y-auto.pr-4") as HTMLElement;
                    if (scrollable) {
                      scrollable.scrollTo({ top: 0, behavior: "smooth" });
                    } else {
                      console.warn("Scroll viewport not found");
                    }
                  }, 200);

                  setIsPreviewMode(true);
                } else {
                  showValidationToast(validationErrors);
                }
              }}
              className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
            >
              Preview Before Submit
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}